/**
 * The experiential technique catalogue — moved from the marketing landing
 * page into the studio. Codes/copy stay in sync with the technique engine;
 * demo previews are prerendered clips/renders shipped as static assets under
 * public/demo/ (no API calls).
 */
import React from "react";
import { ChairDemo } from "./ChairDemo";
import {
  EmptyChairDemo,
  PartsDialogueDemo,
  BusDemo,
  CompassionateLetterDemo,
  NeededWordsDemo,
  SpatialDemo,
  DefusionDemo,
  LoopTapeDemo,
  TapeForwardDemo,
  UrgeSurfingDemo,
  CoupleCycleDemo,
  BrainCastDemo,
  PlaybackDemo,
  TaleDemo,
  InductionDemo,
  SleepStoryDemo,
} from "./VoiceDemos";
import {
  RescriptDemo,
  FeltSenseDemo,
  DeckDemo,
  DistantScreenDemo,
  NightmareDemo,
  PendulationDemo,
  MiracleDemo,
  MonsterDemo,
  BraveDemo,
  CompassionateOtherDemo,
} from "./VisionDemos";

export type TechniqueEntry = {
  code: string;
  modality: "audio" | "vision" | "both";
  title: string;
  body: string;
  wip?: boolean;
};

function Shot({ src, alt, label, className = "" }: { src: string; alt: string; label?: string; className?: string }) {
  return (
    <figure className={`lx-tile lx-shot ${className}`}>
      <img src={src} alt={alt} loading="lazy" />
      {label && <span>{label}</span>}
    </figure>
  );
}

export const LIBRARY: TechniqueEntry[] = [
  {
    code: "EXP-01",
    modality: "audio",
    title: "Schema Chair",
    body: "Chair work, made audible. Give each schema mode a line and a voice — Punitive Parent, Vulnerable Child, Healthy Adult — and play them together. Turn the critic down and the Healthy Adult up, live, while your client listens.",
  },
  {
    code: "EXP-02",
    modality: "vision",
    title: "Imagery Rescripting",
    body: "A remembered scene and its rescription, rendered as symbolic before-and-after images — color, weather and light instead of literal depiction. Show the moment safety returns.",
  },
  {
    code: "EXP-04",
    modality: "audio",
    title: "Spatial Modes",
    body: "The chairs, in 3D audio. Each mode gets a position in space — the critic looming behind, the child small and distant, the Healthy Adult close and centered. Best with headphones.",
  },
  {
    code: "EXP-05",
    modality: "audio",
    title: "Defusion Voice",
    body: "A sticky thought, re-spoken — slowed to a crawl, sped to a squeak, drifting far away. ACT-style defusion: the same words stop sounding like the truth.",
  },
  {
    code: "EXP-06",
    modality: "vision",
    title: "Safe Place",
    body: "The client's safe place, rendered from their own description — an image to ground to, in session and after it.",
  },
  {
    code: "EXP-07",
    modality: "audio",
    title: "Empty Chair",
    body: "The classic Gestalt dialogue with someone who isn't in the room. Compose the exchange turn by turn — the client's words, the imagined reply — and hear it spoken, voice answering voice.",
  },
  {
    code: "EXP-08",
    modality: "audio",
    title: "Parts Dialogue",
    body: "IFS parts work, audible. Exile, Manager, Firefighter and Self each get a line and a voice, speaking in turn — the inner system heard as the conversation it already is.",
  },
  {
    code: "EXP-09",
    modality: "audio",
    title: "Passengers on the Bus",
    body: "The ACT metaphor, made literal. The thoughts heckle all at once — critic, worry, anger — while your client keeps their hands on the wheel. Turn any passenger up or down, live.",
  },
  {
    code: "EXP-10",
    modality: "audio",
    title: "Compassionate Letter",
    body: "The client writes the kind words; a warm voice reads them back. Compassion-focused therapy's core move — hearing your own kindness from the outside.",
  },
  {
    code: "EXP-11",
    modality: "audio",
    title: "The Needed Words",
    body: "The apology, the blessing, the \"I'm proud of you\" that never came — composed together in session and finally heard, in a steady voice.",
  },
  {
    code: "EXP-12",
    modality: "vision",
    title: "Felt-Sense Image",
    body: "Focusing, with a canvas. Render the felt sense as a symbolic image, then refine — warmer, heavier, more like dusk — until the client says: that's it. That's the one.",
  },
  {
    code: "EXP-13",
    modality: "vision",
    title: "Safe Container",
    body: "A container strong enough to hold what's too much to carry between sessions — the vault, the chest at the bottom of the lake — rendered from the client's own description.",
  },
  {
    code: "EXP-14",
    modality: "vision",
    title: "Metaphor Deck",
    body: "A projective card deck, generated on demand. Symbolic images from the session's theme — the client picks the one that resonates, and the conversation follows.",
  },
  {
    code: "EXP-15",
    modality: "vision",
    title: "The Distant Screen",
    body: "Imaginal distancing, watched: the difficult scene re-rendered smaller, farther, drained of color — receding step by step as the client stays grounded here.",
  },
  {
    code: "EXP-16",
    modality: "both",
    title: "Compassionate Other",
    body: "The client's ideal compassionate being, seen and heard — rendered symbolically from their description, speaking the words they most need in a voice they choose.",
  },
  {
    code: "EXP-17",
    modality: "audio",
    title: "The Loop Tape",
    body: "The OCD classic, revived: the obsessional thought spoken once and played on repeat while habituation does its quiet work. What used to be a cassette is now one click.",
  },
  {
    code: "EXP-18",
    modality: "audio",
    title: "Playing the Tape Forward",
    body: "The urge, narrated past the first drink to where the evening actually ends — then the other ending. Motivational interviewing's most-used move, in two takes.",
  },
  {
    code: "EXP-19",
    modality: "audio",
    title: "Urge Surfing",
    body: "The craving as a wave in a paced narration: watch it swell, crest, and pass. You don't have to fight it, and you don't have to obey it.",
  },
  {
    code: "EXP-20",
    modality: "audio",
    title: "The Cycle, Replayed",
    body: "For couples: the pursue–withdraw dance voiced from the outside, so the pattern can be heard instead of had — then the softened version, with the fear underneath spoken aloud.",
  },
  {
    code: "EXP-21",
    modality: "audio",
    title: "The Brain Cast",
    body: "Psychoeducation as radio play: the amygdala sounds every alarm, the cortex checks the facts, the breath slows the room. A panic attack, explained from the inside.",
  },
  {
    code: "EXP-22",
    modality: "audio",
    title: "Playback Theatre",
    body: "The client tells a moment from their week; the voices perform it back, slightly heightened. Drama therapy's core ritual, available in every room.",
  },
  {
    code: "EXP-23",
    modality: "audio",
    title: "Therapeutic Tale",
    body: "A tale written for one listener — the client's predicament wrapped in metaphor and narrated chapter by chapter. Meseterápia, rendered.",
  },
  {
    code: "EXP-24",
    modality: "audio",
    title: "Metaphor Induction",
    body: "A slow, personalized Ericksonian journey in a steady voice, the client's own imagery embedded. No hurry at all on this path.",
  },
  {
    code: "EXP-25",
    modality: "audio",
    title: "Sleep Story",
    body: "The client's own safe place woven into a long wind-down narration — the thing the consumer sleep apps can't do: personal.",
  },
  {
    code: "EXP-26",
    modality: "both",
    title: "The Miracle Question",
    body: "Suppose tonight, while you slept, the problem was solved. The morning after, rendered as an image and read back — solution-focused therapy's founding move.",
  },
  {
    code: "EXP-27",
    modality: "both",
    title: "The Worry Monster",
    body: "For young clients: the worry externalized as a small, friendly, out-arguable creature — seen, voiced, and shrunk with a squeaky voice.",
  },
  {
    code: "EXP-28",
    modality: "both",
    title: "Brave Rehearsal",
    body: "The feared moment as a gentle scene, with a coping voice in the child's corner. Find the desk, take one breath — one brave thing at a time.",
  },
  {
    code: "EXP-29",
    modality: "both",
    title: "Nightmare Rescripting",
    body: "Imagery Rehearsal Therapy: the recurring nightmare rewritten with the client, rendered and narrated both ways, and the new version rehearsed until it's the one that plays at night.",
  },
  {
    code: "EXP-30",
    modality: "both",
    title: "Pendulation",
    body: "Somatic pacing: swing between the resource and the heavy thing, three breaths at a time — the image crossfades, the narration keeps the rhythm, and you always come back to the meadow.",
  },
];
const DEMO = process.env.PUBLIC_URL || "";

export const EXAMPLES: Record<string, React.ReactNode> = {
  "EXP-01": <ChairDemo />,
  "EXP-02": <RescriptDemo />,
  "EXP-04": <SpatialDemo />,
  "EXP-05": <DefusionDemo />,
  "EXP-06": (
    <div className="lib-ex">
      <span className="lib-ex__label">“the lake at my grandmother's, late summer” — real render</span>
      <div className="lx-pair">
        <Shot
          src={`${DEMO}/demo/safe-place/lake.webp`}
          alt="symbolic render of the client's safe place"
          className="lx-tile--wide"
        />
      </div>
    </div>
  ),
  "EXP-07": <EmptyChairDemo />,
  "EXP-08": <PartsDialogueDemo />,
  "EXP-09": <BusDemo />,
  "EXP-10": <CompassionateLetterDemo />,
  "EXP-11": <NeededWordsDemo />,
  "EXP-12": <FeltSenseDemo />,
  "EXP-13": (
    <div className="lib-ex">
      <span className="lib-ex__label">“a bank vault, deep underground, with my name on the only key” — real render</span>
      <div className="lx-pair">
        <Shot
          src={`${DEMO}/demo/safe-container/vault.webp`}
          alt="symbolic render of the client's safe container"
          className="lx-tile--wide"
        />
      </div>
    </div>
  ),
  "EXP-14": <DeckDemo />,
  "EXP-15": <DistantScreenDemo />,
  "EXP-16": <CompassionateOtherDemo />,
  "EXP-17": <LoopTapeDemo />,
  "EXP-18": <TapeForwardDemo />,
  "EXP-19": <UrgeSurfingDemo />,
  "EXP-20": <CoupleCycleDemo />,
  "EXP-21": <BrainCastDemo />,
  "EXP-22": <PlaybackDemo />,
  "EXP-23": <TaleDemo />,
  "EXP-24": <InductionDemo />,
  "EXP-25": <SleepStoryDemo />,
  "EXP-26": <MiracleDemo />,
  "EXP-27": <MonsterDemo />,
  "EXP-28": <BraveDemo />,
  "EXP-29": <NightmareDemo />,
  "EXP-30": <PendulationDemo />,
};
